/**
 * 400 (DECISIONS 396, 400; DIRECTION §0.14 step 1): first person — the person camera path's `eye` stop
 * (rendering/cameraLook.ts) made playable. The camera stands at the sim's own muzzle (core/bullets.ts muzzlePoint: the
 * stance's eye, the lean out of cover, a jump) and looks along the aim, so the screen's centre is the fire line at every
 * range; the player's own figure is hidden; the HUD is today's. A presentation flag, off by default: the Lab's row
 * (`eyeView`) or `?eyeview=1`. On, the wheel zooms in past the shoulder's closest stop to the eye, and out again.
 */

let override: boolean | null = null;

/** The Lab's launch: `null` goes back to the URL / the default (off). */
export function setEyeViewAllowed(on: boolean | null): void { override = on; }

/** Whether the eye stop can be reached: the Lab's override, else the page's `?eyeview=1`. */
export function eyeViewAllowed(): boolean {
  if (override !== null) return override;
  try { return typeof location !== 'undefined' && new URLSearchParams(location.search).get('eyeview') === '1'; } catch { return false; }
}

/** 407 (§0.14 step 2): at the eye, the own arms and gun drawn (rendering/viewmodel.ts) — on with the eye's row; `?viewmodel=0` draws step 1's bare camera (a comparison). */
export function viewmodelAllowed(): boolean {
  try { return typeof location === 'undefined' || new URLSearchParams(location.search).get('viewmodel') !== '0'; } catch { return true; }
}

/**
 * 73.9 step 4 (§0.14): whether a unit's place lets the camera stand at its eye — on foot, or seated out in the open: standing
 * in a hatch (an APC's or a tank's cupola, a technical's pintle) or sitting behind a ground gun, where the sim keeps its eye
 * (`seatPos`; core/bullets.ts muzzlePoint). In a seat the hull or a cab encloses (`enclosed`: the host's insideHull / inCab /
 * inBay), only where the seat has an inside drawn (411, `inside`: rendering/seatInsides.ts). Never boarded.
 */
export function eyeSeatOpen(u: { mount?: unknown; seatPos?: unknown; boarded?: unknown }, enclosed: boolean, inside = false): boolean {
  if (u.boarded) return false;
  if (enclosed) return inside;
  return !u.mount || u.seatPos !== undefined;
}

/**
 * One wheel notch on the person path: at the eye, out (`deltaY` > 0) goes back to the shoulder's closest stop; at the
 * shoulder's closest stop, in goes to the eye. `null`: the notch is the shoulder's own zoom, as before.
 */
export function eyeWheel(atEye: boolean, zoom: number, deltaY: number, zoomMin: number): boolean | null {
  if (atEye) return deltaY > 0 ? false : true;
  return deltaY < 0 && zoom <= zoomMin * (1 + 1e-9) ? true : null;
}

/**
 * 407 (DECISIONS 407; DIRECTION §0.14 step 2): the view kick on the player's own shot at the eye — the camera pitches up and
 * a little aside and settles, with the drawn gun's own kick (gunShotPose) in the viewmodel. Drawn only: the aim is the
 * player's input and stays untouched (decision 16: a host-side kick on the input would be host-computed state; a recoil that
 * moves where shots land is the sim's, §0.14 step 8, if play asks for it), so the crosshair, on the fire line, shows the
 * kick against it. Per weapon in hand: the pitch at the kick's peak and the aside's most (radians); the kick peaks about
 * `rise` × 2 after the shot and settles over `settle`.
 */
export const VIEW_KICK = {
  rifle: { pitch: 0.016, aside: 0.005 }, pistol: { pitch: 0.026, aside: 0.008 }, rocket: { pitch: 0.05, aside: 0.012 }, gl40: { pitch: 0.04, aside: 0.01 },
  rise: 0.025, settle: 0.12,
};
export type ViewKickWeapon = 'rifle' | 'pistol' | 'rocket' | 'gl40';
/** The kick's state: each axis a slow and a fast decay (their difference the kick: a quick rise, a slower settle), and the shots so far (the aside's sequence). */
export interface ViewKick { slowP: number; fastP: number; slowY: number; fastY: number; shots: number }
export function createViewKick(): ViewKick { return { slowP: 0, fastP: 0, slowY: 0, fastY: 0, shots: 0 }; }
/** The two decays' difference at its peak, for an impulse of 1 (so a weapon's `pitch` is the kick's peak). */
const KICK_PEAK = (() => {
  const r = VIEW_KICK.rise, s = VIEW_KICK.settle, t = (r * s / (s - r)) * Math.log(s / r);
  return Math.exp(-t / s) - Math.exp(-t / r);
})();
/** A shot: the kick's impulse — up by the weapon's pitch, aside by its most times a step along the golden ratio's sequence (−1 … 1: shot to shot, never the same side twice running in a pattern). Shots in a burst add. */
export function kickView(k: ViewKick, weapon: ViewKickWeapon): void {
  const w = VIEW_KICK[weapon], side = (((k.shots++ * 0.6180339887) % 1) * 2 - 1);
  const p = w.pitch / KICK_PEAK, y = (w.aside * side) / KICK_PEAK;
  k.slowP += p; k.fastP += p; k.slowY += y; k.fastY += y;
}
/** Advance the kick by `dt` seconds; returns its pitch (up) and yaw (to the right) now, radians. */
export function stepViewKick(k: ViewKick, dt: number): { pitch: number; yaw: number } {
  const ds = Math.exp(-dt / VIEW_KICK.settle), df = Math.exp(-dt / VIEW_KICK.rise);
  k.slowP *= ds; k.fastP *= df; k.slowY *= ds; k.fastY *= df;
  if (Math.abs(k.slowP) < 1e-7 && Math.abs(k.slowY) < 1e-7) { k.slowP = k.fastP = k.slowY = k.fastY = 0; }
  return { pitch: k.slowP - k.fastP, yaw: k.slowY - k.fastY };
}
